import Core                           from '@/core/Core';

import type RpcDataPagination          from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort                from '@/core/services/Rpc/RpcDataSort';
import type BasePageFilter             from '@/domains/BasePageFilter/BasePageFilter';

/**
 * Параметры запроса от грида
 */
export type DataGridRequestParams = {
  filters: Record<string, unknown>;
  pagination: Partial<RpcDataPagination> | null;
  sort: Partial<RpcDataSort> | null;
};

/**
 * Результат пагинированного запроса
 */
type PaginatedResult<TEntity> = {
  data: TEntity[];
  pagination: RpcDataPagination | null;
  /** Запрос отменён через abortPrevious — результат не содержит данных и должен быть проигнорирован */
  cancelled?: boolean;
};

/**
 * Сигнатура сервисного метода
 */
export type ServiceMethod<TParams, TEntity> = (
  params: TParams
) => Promise<PaginatedResult<TEntity>>;

/**
 * Источник дополнительных параметров — объект или функция-геттер.
 */
type ReactiveSource =
  | Record<string, unknown>
  | (() => Record<string, unknown>);

/**
 * DataGridLoader — связующее звено между VDataGridV2 и доменным сервисом.
 *
 * Берёт на себя:
 * - Хранение данных, пагинации, состояния загрузки
 * - Сборку параметров (гридовые фильтры + registry + reactive + externalFilter)
 * - Вызов сервисного метода
 * - Подписку на изменения внешних источников
 *
 * Источники параметров:
 * 1. filters       — приходят от грида через onParamsChange
 * 2. registry      — localStorage, страничный фильтр, useRegistryFilters
 * 3. reactive      — реактивные значения из компонента, useReactiveSource / useExtraParams
 * 4. externalFilter — фильтр страницы, синхронный с фильтрами колонок, useExternalFilter
 *
 * Использование:
 *
 *   // Простой реестр
 *   loader: new DataGridLoader(PartnersService.getListPaginated)
 *
 *   // Со статичными доп. параметрами
 *   loader: new DataGridLoader(AssembliesService.getListPaginated)
 *     .useExtraParams({ requireCommentsInfo: true })
 *
 *   // С реактивными параметрами из компонента
 *   loader: new DataGridLoader(TendersService.getListPaginated)
 *     .useReactiveSource(() => ({
 *         tabId: this.tabId,
 *         pattern: this.pattern,
 *     }))

 *
 *   // С фильтром реестра из localStorage
 *   loader: new DataGridLoader(TransportOrdersService.getListPaginated)
 *     .useRegistryFilters(TransportOrdersPageFilter)
 *
 *   // Комбинация
 *   loader: new DataGridLoader(TendersService.getListPaginated)
 *     .useReactiveSource(() => ({ tabId: this.tabId }))
 *     .useRegistryFilters(TendersPageFilter)
 */
class DataGridLoader<TParams = any, TEntity = any> {

  /** Данные для отображения в гриде */
  data: TEntity[] = [];

  /** Текущая пагинация */
  pagination: RpcDataPagination = { limit: 50, offset: 0, total: 0 };

  /** Флаг загрузки */
  isLoading: boolean = false;

  /**
   * Класс фильтра реестра.
   * Хранит состояние в localStorage, при каждом запросе читает актуальные данные.
   */
  private registryFilterClass: typeof BasePageFilter | null = null;

  /** Сервисный метод */
  private serviceMethod: ServiceMethod<TParams, TEntity>;

  /**
   * Реактивные источники доп. параметров.
   * Каждый — объект (статика, реактивный объект) или функция-геттер.
   *
   * Массив, а не одно поле: useReactiveSource / useExtraParams можно звать
   * в цепочке сколько угодно раз, источники не затирают друг друга.
   * Применяются в порядке регистрации — при коллизии ключа побеждает последний.
   */
  private reactiveSources: ReactiveSource[] = [];

  /**
   * Последние фильтры от грида.
   * Публичное поле — компонент может читать его в computed для зависимых фильтров
   * (например, опции одного селектора зависят от значения другого).
   */
  filters: Record<string, unknown> = {};

  /** Последняя сортировка от грида */
  private gridSort: Partial<RpcDataSort> | null = null;

  /** Последняя пагинация от грида */
  private gridPagination: Partial<RpcDataPagination> | null = null;

  /** Пагинация отключена — слать в запрос isEnabled: false, бэк вернёт весь список */
  private isPaginationDisabled: boolean = false;

  /** Флаг: грид уже хотя бы один раз передал параметры */
  private hasReceivedGridParams: boolean = false;

  /** Функции отписки от watch — по одной на каждый реактивный источник */
  private unwatchReactiveSources: Array<() => void> = [];

  /**
   * Внешнее изменение уже запланировано на этот тик.
   * Источников несколько, и одно действие пользователя (смена таба) может
   * сдвинуть сразу два — без флага каждый watch дал бы свой запрос.
   */
  private isExternalChangeScheduled: boolean = false;

  /** Фильтр страницы, синхронный с фильтрами колонок (useExternalFilter). null — не подключён */
  private externalFilterSource: (() => BasePageFilter | null) | null = null;

  /** Колбэк: фильтр страницы изменился из колонки грида (например, обновить счётчики) */
  private onExternalFilterUpdateCallback: (() => void) | null = null;

  /** Лоадер уничтожен — отложенный колбэк выполнять не нужно */
  private isDestroyed: boolean = false;

  /**
   * Кэш уже предупреждённых коллизий ключей.
   * Чтобы не спамить Core.warn на каждый запрос — каждая комбинация
   * "ключ + источники" предупреждается один раз за жизнь лоадера.
   */
  private warnedCollisions: Set<string> = new Set();

  /**
   * Колбэк после успешной загрузки. Существует чтобы потребитель мог
   * достать из ответа поля, специфичные для своего домена (meta, time, агрегаты),
   * — лоадер по контракту знает только про `data` и `pagination`.
   */
  private onAfterLoadCallback: ((result: PaginatedResult<any>) => void);

  /**
   * метод грида, сбрасывает offset,
   * нужен тут, потому что offset принадлежит гриду, лоадер его только получает
   */
  private onExternalParamsChangeCallback: (() => void) | null = null;

  constructor(serviceMethod: ServiceMethod<TParams, TEntity>) {
    this.serviceMethod = serviceMethod;

    // Делаем экземпляр реактивным для Vue
    return reactive(this) as this;
  }

  // ==================== Публичные методы ====================

  /**
   * Подключить фильтр реестра из localStorage.
   * При каждом запросе грида фильтр десериализуется из хранилища —
   * всегда актуальные данные без подписок и отслеживания состояний.
   *
   * Принимает null — тогда фильтр не подключается. Это позволяет
   * переиспользуемым реестрам вызывать метод в цепочке без внешнего if,
   * передавая опциональный проп напрямую.
   *
   * Использование:
   *   loader: new DataGridLoader(Service.getListPaginated)
   *     .useRegistryFilters(TransportOrdersPageFilter)
   *
   *   // С опциональным фильтром из пропа
   *   loader: new DataGridLoader(Service.getListPaginated)
   *     .useRegistryFilters(this.filterClass)
   */
  useRegistryFilters(FilterClass: typeof BasePageFilter | null | undefined): this {
    this.registryFilterClass = FilterClass ?? null;
    return this;
  }

  /**
   * Подключить реактивный источник дополнительных параметров.
   *
   * Принимает функцию-геттер или объект:
   * - Функция вызывается при каждом запросе, изменения автоматически
   *   триггерят refresh — используется когда параметры зависят от
   *   значений из this (this.tabId, this.partnerId и т.д.).
   * - Объект используется когда передаётся уже реактивный источник
   *   (например, this.$props) или константа.
   *
   * Для статичных констант семантически чище использовать useExtraParams.
   *
   * Вызывать можно многократно — источники накапливаются, а не заменяют
   * друг друга. При совпадении ключей побеждает зарегистрированный позже.
   *
   * Использование:
   *   loader: new DataGridLoader(TendersService.getListPaginated)
   *     .useReactiveSource(() => ({
   *         tabId: this.tabId,
   *         pattern: this.pattern,
   *     }))
   */
  useReactiveSource(source: ReactiveSource): this {
    this.reactiveSources.push(source);

    let watchTarget: () => unknown;

    if (typeof source === 'function') {
      watchTarget = source;
    }
    else {
      watchTarget = () => source;
    }

    const unwatch = watch(
      watchTarget,
      () => this.handleExternalParamsChange(),
      { deep: true },
    );

    this.unwatchReactiveSources.push(unwatch);

    return this;
  }

  /**
   * Статичные дополнительные параметры запроса.
   * Синоним useReactiveSource — отличается только семантикой имени,
   * чтобы слово "reactive" не сбивало с толку при передаче констант.
   *
   * Использование:
   *   loader: new DataGridLoader(AssembliesService.getListPaginated)
   *     .useExtraParams({ requireCommentsInfo: true })
   */
  useExtraParams(params: Record<string, unknown>): this {
    return this.useReactiveSource(params);
  }

  /**
   * Отключить пагинацию для запроса.
   * Без этого метода лоадер шлёт пагинацию от грида (по умолчанию limit: 50),
   * и бэк обрезает список. С ним в запрос уйдёт { isEnabled: false } —
   * бэк вернёт весь список целиком.
   *
   * Использование:
   *   loader: new DataGridLoader(Service.getListPaginated)
   *     .useExtraParams({ product })
   *     .withoutPagination()
   */
  withoutPagination(): this {
    this.isPaginationDisabled = true;
    return this;
  }

  /** грид оставляет тут свой метод, позовём при внешнем триггере (смена таба) */

  onExternalParamsChange(callback: () => void): this {
    this.onExternalParamsChangeCallback = callback;
    return this;
  }

  /**
   * Фильтр страницы (справа сверху), синхронный с фильтрами колонок грида.
   * Вся синхронизация — здесь:
   *
   *   колонки → фильтр: перед каждой загрузкой лоадер кладёт фильтры колонок
   *     в фильтр (filter.update). Запрос уходит, когда колонки и фильтр уже одинаковые.
   *
   *   фильтр → колонки: лоадер следит за параметрами фильтра. Поменялось поле,
   *     которого в колонках нет или там другое значение, — фильтр поменяли снаружи:
   *     грид кладёт значения фильтра в колонки и грузит. Если все изменённые поля
   *     уже лежат в колонках — это мы сами положили их из колонки, грузить нечего.
   *
   * Параметры фильтра идут в запрос последними: при совпадении поля побеждает фильтр.
   *
   * Использование:
   *   loader: new DataGridLoader(Service.getListPaginated)
   *     .useExternalFilter(() => this.filter)
   *     .onExternalFilterUpdate(() => { this.$emit('orders-page-filter:updated'); })
   */
  useExternalFilter(source: () => BasePageFilter | null): this {
    this.externalFilterSource = source;

    const unwatch = watch(
      () => this.getExternalFilterParams(),
      (next, previous) => {
        if (this.isAlreadyInColumns(next, previous) === false) {
          this.handleExternalParamsChange();
        }
      },
      { deep: true },
    );

    this.unwatchReactiveSources.push(unwatch);

    return this;
  }

  /**
   * Колбэк: фильтр страницы изменился из колонки грида. Нужен странице, чтобы
   * отреагировать на новое значение (например, обновить счётчики вкладок).
   */
  onExternalFilterUpdate(callback: () => void): this {
    this.onExternalFilterUpdateCallback = callback;
    return this;
  }

  /** Фильтр страницы (useExternalFilter); null — не подключён */
  getExternalFilter(): BasePageFilter | null {
    return this.externalFilterSource?.() ?? null;
  }

  /** Параметры фильтра страницы для запроса; {} — фильтр не подключён */
  getExternalFilterParams(): Record<string, unknown> {
    return this.getExternalFilter()?.getRequestParams() ?? {};
  }

  /**
   * Вызывается гридом при изменении фильтров, сортировки или пагинации.
   *
   * - filters: фильтры в виде для запроса — ключ filter-send-name колонки, значение
   *   переведено по filter-identity (select усечён до id, поля опции развёрнуты в корень и т.д.)
   * - sort: текущая сортировка или null
   * - pagination: текущая пагинация или null
   * - filtersRaw: те же фильтры, но как лежат в контролах шапки — ключ id колонки,
   *   значение целиком (объект поставщика, а не его id). До загрузки кладутся
   *   в фильтр страницы (useExternalFilter), чтобы запрос ушёл уже с ними
   */
  onParamsChange(
    filters: Record<string, unknown>,
    sort: Partial<RpcDataSort> | null,
    pagination: Partial<RpcDataPagination> | null,
    filtersRaw: Record<string, unknown> = {},
  ): void {
    this.updateExternalFilter(filtersRaw);

    this.filters = filters;
    this.gridSort = sort;
    this.gridPagination = pagination;
    this.hasReceivedGridParams = true;

    // Проблема: когда пользователь устанавливает фильтр, то реестр может визуально не измениться
    // из этого складывается впечатление, что ничего не произошло. Это реальный юзер экспириуенс, я не выдумываю.
    this.data = [];

    this.loadData();
  }

  /**
   * Перезагрузить данные с текущими параметрами.
   * Используется при изменении внешних фильтров или после действий (создание, удаление).
   */
  refresh(): Promise<void> {
    return this.loadData();
  }

  /**
   * Очистка подписок. Вызывается гридом в beforeUnmount.
   */
  destroy(): void {
    this.isDestroyed = true;

    for (const unwatch of this.unwatchReactiveSources) {
      unwatch();
    }

    this.unwatchReactiveSources = [];
  }

  mergeData(newItem: TEntity): void;
  mergeData(newItems: TEntity[]): void;
  /**
   * Точечное обновление существующих записей по id.
   * Принимает одну запись или массив. Новые и удалённые записи игнорируются —
   * для этого refresh().
   *
   * Применение: мягкий polling с updatedAfter — обновляет данные
   * без перерендера, сохраняя UI-state (открытые попапы, scroll).
   */
  mergeData(input: TEntity | TEntity[]): void {
    const newItems = Array.isArray(input) ? input : [input];
    if (newItems.length === 0) return;

    for (const newItem of newItems) {
      const id = (newItem as any).id;
      if (id === undefined || id === null) continue;

      const existing = this.data.find(item => (item as any).id === id);

      if (existing) {
        // Мутация, а не замена — сохраняет ссылки на объект.
        // Если у строки открыт попап со ссылкой на эту запись,
        // замена объекта оставит попап со старыми данными.
        Object.assign(existing as any, newItem);
      }
    }
  }

  onAfterLoad(callback: (result: PaginatedResult<any>) => void): this {
    this.onAfterLoadCallback = callback;
    return this;
  }

  // ==================== Приватные методы ====================

  /** сменили таб - обнулили кеш и попали на страницу 1,
   *  без колбека обычный рефреш
   * */

  private handleExternalParamsChange(): void {
    if (this.isExternalChangeScheduled === true) {
      return;
    }

    this.isExternalChangeScheduled = true;

    // Откладываем на микротаск: все watch этого тика успеют отработать,
    // запрос уйдёт один и уже с полным набором изменившихся параметров.
    nextTick(() => {
      this.isExternalChangeScheduled = false;

      if (this.isDestroyed === true) {
        return;
      }

      if (this.onExternalParamsChangeCallback === null) {
        this.refresh();
      }
      else {
        this.onExternalParamsChangeCallback();
      }
    });
  }

  /**
   * Все изменённые поля фильтра уже лежат в фильтрах колонок с тем же значением?
   * Так бывает, когда фильтр поменяли мы сами из колонки (updateExternalFilter) —
   * запрос с этим значением уже ушёл. Поле, которого в колонках нет («Образцы»),
   * или с другим значением — значит, фильтр поменяли снаружи.
   */
  private isAlreadyInColumns(next: Record<string, unknown>, previous: Record<string, unknown>): boolean {
    const keys = new Set([...Object.keys(next), ...Object.keys(previous)]);

    for (const key of keys) {
      if (Core.utils.isEqualById(next[key], previous[key]) === true) {
        // поле не менялось
      }
      else if (Core.utils.isEqualById(next[key], this.filters[key]) === false) {
        return false;
      }
    }

    return true;
  }

  /**
   * Колонки → фильтр страницы: кладём фильтры колонок в фильтр (поля, которых у фильтра
   * нет, update пропустит). Если фильтр поменялся — сообщаем странице.
   */
  private updateExternalFilter(filtersRaw: Record<string, unknown>): void {
    const filter = this.getExternalFilter();

    if (filter === null) {
      return;
    }

    const isFilterChanged = filter.update(filtersRaw);

    if (isFilterChanged === true) {
      this.onExternalFilterUpdateCallback?.();
    }
  }

  /**
   * Загрузить данные из сервиса
   */
  private async loadData(): Promise<void> {
    if (!this.hasReceivedGridParams) return;

    this.isLoading = true;

    const params = this.buildParams();

    const result = await this.serviceMethod(params);

    if (result.cancelled) {
      // Запрос отменён через abortPrevious — пустой результат не применяем,
      // isLoading не сбрасываем: актуальные данные придут из нового запроса
      return;
    }

    this.data = result.data;

    if (result.pagination) {
      this.pagination = result.pagination;
    }

    this.onAfterLoadCallback?.(result);

    this.isLoading = false;
  }

  /**
   * Собирает финальные параметры запроса:
   * гридовые фильтры + registry + reactive + externalFilter + sort + pagination.
   *
   * При коллизии ключа между источниками — Core.warn один раз на лоадер.
   * Порядок применения (последний побеждает):
   *   filters → registry → reactive → externalFilter
   */
  private buildParams(): TParams {
    const sources: Array<[string, Record<string, unknown>]> = [
      ['filters',     this.filters],
      ['registry',    this.getRegistryFilters()],
      ['reactive',    this.getReactiveParams()],
      ['externalFilter', this.getExternalFilterParams()],
    ];

    const merged: Record<string, unknown> = {};
    const origin: Record<string, string> = {};

    for (const [sourceName, params] of sources) {
      for (const [key, value] of Object.entries(params)) {
        if (key in merged) {
          this.warnKeyCollision(key, origin[key], sourceName, merged[key], value);
        }
        merged[key] = value;
        origin[key] = sourceName;
      }
    }

    let pagination: Partial<RpcDataPagination> | null;

    if (this.isPaginationDisabled) {
      pagination = { isEnabled: false };
    }
    else {
      pagination = this.gridPagination;
    }

    return {
      ...merged,
      pagination,
      sort: this.gridSort,
    } as TParams;
  }

  /**
   * Предупредить о коллизии ключа между источниками.
   * Каждая уникальная пара (ключ + источники) предупреждается один раз
   * за жизнь лоадера, чтобы не спамить на каждый запрос.
   *
   * Одинаковое значение — не коллизия: перетирать нечего. Так бывает, когда источники
   * синхронизированы (поставщик в фильтре колонки и в фильтре страницы, см. useExternalFilter).
   */
  private warnKeyCollision(
    key: string,
    previousSource: string,
    currentSource: string,
    previousValue: unknown,
    currentValue: unknown,
  ): void {
    if (Core.utils.isEqualById(previousValue, currentValue) === true) {
      return;
    }

    const cacheKey = `${key}:${previousSource}->${currentSource}`;
    if (this.warnedCollisions.has(cacheKey)) {
      return;
    } else {
      this.warnedCollisions.add(cacheKey);
    }

    Core.warn(
      `DataGridLoader: коллизия параметра "${key}" между источниками `
      + `"${previousSource}" и "${currentSource}". `
      + `Победил "${currentSource}".`,
      {
        key,
        previousSource,
        previousValue,
        currentSource,
        currentValue,
      },
    );
  }

  /**
   * Получить параметры фильтра реестра.
   * Каждый вызов читает из localStorage — гарантия актуальности.
   */
  private getRegistryFilters(): Record<string, unknown> {
    if (!this.registryFilterClass) return {};

    return this.registryFilterClass.fromLocalStorage().getRequestParams();
  }

  /**
   * Получить параметры из реактивных источников.
   * Разруливает форму каждого источника — функция или объект — и мержит
   * их в порядке регистрации.
   *
   * Коллизии между самими источниками тоже логируются: если два вызова
   * useReactiveSource отдают один ключ, тихо победит последний, и это
   * ровно тот случай, который снаружи выглядит как "параметр не уходит".
   */
  private getReactiveParams(): Record<string, unknown> {
    const merged: Record<string, unknown> = {};
    const origin: Record<string, string> = {};

    for (const [index, source] of this.reactiveSources.entries()) {
      let params: Record<string, unknown>;

      if (typeof source === 'function') {
        params = source();
      }
      else {
        params = source;
      }

      const sourceName = `reactive[${index}]`;

      for (const [key, value] of Object.entries(params)) {
        if (key in merged) {
          this.warnKeyCollision(key, origin[key], sourceName, merged[key], value);
        }

        merged[key] = value;
        origin[key] = sourceName;
      }
    }

    return merged;
  }
}

export default DataGridLoader;